import { ReactNode, useEffect } from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";

import i18n from "../config/i18n";
import store from "../store/store";
import queryClient from "../config/queryClient";
import { AuthProvider } from "./AuthProvider";
import { RecordQuerySyncProvider } from "./RecordQuerySyncProvider";
import { ModalRuntimeProvider } from "modules/record/shared/modal-runtime/ModalRuntimeProvider";
import { PlatformThemeProvider } from "./PlatformThemeProvider";
import { fetchTranslationResolve } from "app/hooks/useTranslationResolver";

type AppProviderProps = {
    children: ReactNode;
};

const AppProvider = ({ children }: AppProviderProps) => {
    useEffect(() => {
        let active = true;
        const updateDocumentLanguage = async (language?: string) => {
            const requested = language || i18n.resolvedLanguage || i18n.language;
            const response = await fetchTranslationResolve([], requested);
            const resolved = response.meta?.language;
            if (!active || typeof resolved !== "string" || !resolved) return;

            document.documentElement.lang = resolved;
        };

        void updateDocumentLanguage();
        const handleLanguageChanged = (language: string) => { void updateDocumentLanguage(language); };
        i18n.on("languageChanged", handleLanguageChanged);
        return () => {
            active = false;
            i18n.off("languageChanged", handleLanguageChanged);
        };
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <I18nextProvider i18n={i18n}>
                    <PlatformThemeProvider>
                        <Provider store={store}>
                            <RecordQuerySyncProvider>
                                <ModalRuntimeProvider>
                                    {children}
                                </ModalRuntimeProvider>
                            </RecordQuerySyncProvider>
                        </Provider>
                    </PlatformThemeProvider>
                </I18nextProvider>
            </AuthProvider>
        </QueryClientProvider >
    );
};

export default AppProvider;
